import React from 'react';
import { useSelector } from 'react-redux';
import { Tooltip } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { aiAlertNotificationRulesSelector } from '@src/store/common/selectors';
import { CURRENT_PRODUCT_MODE } from '@src/utils/constants';
import { fetchGlobalMute, GlobalMuteRow } from '@src/api/globalMute';
import { getGlobalMuteProductLabel } from './productLabels';
import { OPEN_GLOBAL_MUTE_EVENT } from './index';

// Compact "notifications muted" indicator for the AI Alerts / Manual Alerts
// headers. Kept small so it sits inline (e.g. in the gap before Insights)
// rather than as a full-width banner. Clicking opens the Global Mute drawer
// (same window event the pulse-nav Settings menu dispatches) where it can be
// cleared.
const GlobalMuteBanner = () => {
  const rulesEnabled = useSelector(aiAlertNotificationRulesSelector);
  const currentMode = (CURRENT_PRODUCT_MODE || 'EI').toLowerCase();

  const { data: globalMute } = useQuery<GlobalMuteRow | null>(
    ['global-mute', currentMode],
    async () => {
      const res = await fetchGlobalMute(currentMode);
      return (res?.data?.data ?? null) as GlobalMuteRow | null;
    },
    { enabled: Boolean(rulesEnabled) },
  );

  if (!rulesEnabled || !globalMute?.isActive) {
    return null;
  }

  const productLabel = getGlobalMuteProductLabel(currentMode);
  const alertScope = currentMode === 'app' ? 'AI alert' : 'AI and manual alert';
  const until = moment(globalMute.muteUntil).format('MMM D, YYYY h:mm A');

  const openGlobalMute = () => {
    window.dispatchEvent(new CustomEvent(OPEN_GLOBAL_MUTE_EVENT));
  };

  return (
    <Tooltip
      title={`All ${alertScope} notifications for ${productLabel} are suppressed until ${until}. Clear it anytime from Settings > Global Mute.`}
    >
      <span
        role="button"
        onClick={openGlobalMute}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          cursor: 'pointer',
          color: '#d46b08',
          background: '#fff7e6',
          border: '1px solid #ffd591',
          borderRadius: 6,
          padding: '4px 10px',
          whiteSpace: 'nowrap',
          fontWeight: 500,
          lineHeight: 1,
        }}
      >
        <ExclamationCircleFilled style={{ color: '#faad14' }} />
        Notifications muted
      </span>
    </Tooltip>
  );
};

export default GlobalMuteBanner;
